<template>
  <b-card no-body>
    <b-card-body id="pickup-card-body">
      <pickup-filter :values="filterValues" :filter-submitting="isLoading" @filter="setFilter" />
      <vue-good-table
        mode="remote"
        :sort-options="{ enabled: false }"
        :is-loading.sync="isLoading"
        :show-footer="true"
        :total-rows="totalRecords"
        :pagination-options="
          getPaginationOptions(filter.limit, filter.page, {
            ofLabel: `/ \u00A0`,
            rowsPerPageLabel: `${t(
              'Total number of records'
            )}: ${totalRecords}\u00A0\u00A0\u00A0\u00A0 | \u00A0\u00A0\u00A0\u00A0${t(
              'Rows per page'
            )}`
          })
        "
        :rows="rows"
        :columns="getColumns"
        @on-page-change="onPageChange"
        @on-per-page-change="onPerPageChange"
      >
        <table-no-result-found slot="emptystate" />
        <template slot="table-row" slot-scope="props">
          <span v-if="props.column.field === 'created_at'">
            {{ props.row.created_at ? formatDateToCustom(props.row.created_at) : '-' }}
          </span>
          <span v-else-if="props.column.field === 'customer.name'">
            {{ props.row.customer.name }}
          </span>
          <span v-else-if="props.column.field === 'address.name'">
            {{ props.row.address.name }}
          </span>
          <span v-else-if="props.column.field === 'address.state'">
            {{
              props.row.address.stateName ||
              (props.row.address.state ? props.row.address.state.name : '-')
            }}
          </span>
          <span v-else-if="props.column.field === 'address.city'">
            {{
              props.row.address.cityName ||
              (props.row.address.city ? props.row.address.city.name : '-')
            }}
          </span>
          <span v-else-if="props.column.field === 'consignment_count'">
            {{ props.row.consignment_count }}
          </span>
          <span v-else-if="props.column.field === 'dropped_off_count'">
            {{ props.row.dropped_off_count }}
          </span>
          <span v-else-if="props.column.field === 'status'">
            <b-badge :variant="statusVariant(props.row.status)">
              {{ getPickupStatusText(props.row.status) || '-' }}
            </b-badge>
          </span>
          <span v-if="props.column.field === 'actions'">
            <span class="d-flex justify-content-center table-icon-buttons">
              <b-button
                :id="`detail-button-${props.row.id}`"
                variant="primary"
                type="button"
                size="sm"
                @click="detailPickup(props.row.id)"
              >
                <feather-icon icon="EyeIcon" size="15" />
              </b-button>
              <b-button
                :id="`edit-button-${props.row.id}`"
                variant="primary"
                type="button"
                size="sm"
                @click="editPickup(props.row.id)"
              >
                <feather-icon icon="Edit3Icon" size="15" />
              </b-button>
              <b-button
                :id="`cancel-button-${props.row.id}`"
                variant="danger"
                type="button"
                size="sm"
                @click="cancelPickup(props.row.id)"
              >
                <feather-icon icon="XIcon" size="15" />
              </b-button>
            </span>
          </span>
        </template>
      </vue-good-table>
      <AddPickupModal
        ref="callCourierForm"
        :address_book_list="address_book_list"
        :title="t('Call Courier')"
        @triggerList="loadItems"
      />
    </b-card-body>
  </b-card>
</template>

<script>
import { BCard, BCardBody, BButton, BBadge } from 'bootstrap-vue';
import { VueGoodTable } from 'vue-good-table';
import { useUtils as useI18nUtils } from '@core/libs/i18n';
import { helper } from '@core/mixins/helper.js';
import { vueGoodTablePagination } from '@core/mixins/vueGoodTablePagination';
import PickupFilter from '@/views/pages/pickups/PickupFilter.vue';
import useService from '@/auth/service/useService';
import AddPickupModal from '@/components/AddPickupModal.vue';
import { mapGetters } from 'vuex';

export default {
  components: {
    AddPickupModal,
    BCard,
    BCardBody,
    BButton,
    BBadge,

    VueGoodTable,
    PickupFilter
  },
  mixins: [helper, vueGoodTablePagination],

  data() {
    return {
      isLoading: false,
      totalRecords: 0,
      rows: [],
      pickupDetailData: [],
      filterValues: {
        customer_id: '',
        addressName: '',
        appointmentStart: '',
        appointmentEnd: '',
        status: ''
      },
      consignmentStatuses: {},
      filter: {
        page: 1,
        limit: 10
      },
      address_book_list: []
    };
  },
  computed: {
    ...mapGetters({
      pickupStatus: 'app/pickupStatus',
      countries: 'app/countries'
    }),
    getColumns() {
      if (!this.rows.length) {
        return [];
      }

      return [
        { label: this.$t('Request Date'), field: 'created_at' },
        { label: this.$t('Customer Name'), field: 'customer.name' },
        { label: this.$t('Consignor Name'), field: 'address.name' },
        { label: this.$t('State'), field: 'address.state' },
        { label: this.$t('District'), field: 'address.city' },
        { label: this.$t('Requested Quantity'), field: 'consignment_count' },
        { label: this.$t('Quantity Received'), field: 'dropped_off_count' },
        { label: this.$t('Status'), field: 'status' },
        { label: this.$t('Actions'), field: 'actions' }
      ];
    }
  },
  async created() {
    if (this.pickupStatus.length < 1) {
      await this.$store.dispatch('app/getPickupStatusLabels');
    }
    if (this.countries.length < 1) {
      await this.$store.dispatch('app/getCountries');
    }
    await this.getAllAddressBookList();
    this.loadItems();
  },
  methods: {
    updateParams(newProps) {
      this.filter = { ...this.filter, ...newProps };
    },
    onPageChange(params) {
      this.updateParams({ page: params.currentPage });
      this.loadItems();
    },
    onPerPageChange(params) {
      this.filter.page = 1;
      this.updateParams({ limit: params.currentPerPage });
      this.loadItems();
    },
    loadItems() {
      let requestData = this.filter;
      requestData = {
        ...requestData,
        ...this.getFilteredObject(this.filterValues)
      };

      this.isLoading = true;

      useService.pickupService
        .getAllPickups(requestData)
        .then(response => {
          this.rows = response.data;
          this.totalRecords = (response.meta && response.meta.total) || 0;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    setFilter(data) {
      if (data) {
        this.filterValues = data;
        this.filter.page = 1;
        this.loadItems();
      } else {
        this.filterValues = {
          status: '',
          customer_id: '',
          addressName: '',
          appointmentStart: '',
          appointmentEnd: ''
        };
        this.filter.page = 1;
        this.loadItems();
      }
    },
    detailPickup(id) {
      this.$router.push(`/pickups/${id}`);
    },
    editPickup(id) {
      const item = this.rows.find(row => row.id === id);

      this.$bvModal.show('add-pickup-modal');
      const mappedData = {
        id,
        country: item.address.country,
        state: item.address.state,
        city: item.address.city,
        name: item.address.name,
        address: item.address.formatted,
        appointmentStart: this.formatDateToCustom(item.appointment.start),
        appointmentEnd: this.formatDateToCustom(item.appointment.end)
      };

      setTimeout(() => {
        this.$refs.callCourierForm.setPickupAddressData(mappedData);
      }, 250);
    },

    cancelPickup(id) {
      this.$swal({
        title: `${this.$t('Warning')}!`,
        text: `${this.$t(
          'Your courier request will be canceled. Please enter your cancellation reason.'
        )}`,
        input: 'text',
        inputAttributes: {
          'aria-label': this.$t('Type your reason here...')
        },
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: `${this.$t('Yes')}`,
        cancelButtonText: `${this.$t('No')}`,
        showLoaderOnConfirm: true,
        allowOutsideClick: () => !this.$swal.isLoading(),
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1'
        },
        buttonsStyling: false,
        preConfirm: reason => {
          if (!reason) {
            this.$swal.showValidationMessage(this.$t('Cancellation reason is required'));
            return false;
          }
          return useService.pickupService
            .cancelPickup(id, { cancellation_reason: reason })
            .then(response => {
              this.showSuccessMessage(response.message);
              this.loadItems();
            })
            .catch(messages => {
              if (typeof messages === 'object' && messages && messages.length) {
                messages.forEach(item => {
                  this.showErrorMessage(item.message);
                });
              }
            });
        }
      });
    },

    getAllAddressBookList() {
      useService
        .getAllWarehouses({
          page: 1,
          limit: 1000
        })
        .then(response => {
          this.address_book_list = response.data.map(item => {
            return {
              ...item,
              text: item.name,
              value: item.id
            };
          });
        });
    },
    statusVariant(status) {
      switch (status) {
        case 'pending':
          return 'secondary';
        case 'in_progress':
          return 'warning';
        case 'completed':
          return 'success';
        case 'cancelled':
          return 'danger';
        default:
          return 'dark';
      }
    },

    getPickupStatusText(status) {
      const item = this.pickupStatus.find(x => x.value === status);

      if (item) {
        return item.text;
      }

      return '';
    }
  },
  setup() {
    const { t } = useI18nUtils();

    return {
      t
    };
  }
};
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';
</style>
